/**
 * 부트 3상태 — **브랜드 박힌 봉인 유니온.** (docs/decisions/0008 §부트 3상태)
 *
 * ─── `null` 을 거부하는 이유 ───
 * `null` 이 위험한 건 나쁜 값이라서가 아니라 **누구나 만들 수 있어서**다.
 * `bootstrap().catch(() => null)` 한 줄이 "네트워크 오류"를 "미인증"으로 붕괴시키고,
 * 그 상태의 화면은 로그인 안내다 — 서버가 죽은 날 전 사용자가 자기 비밀번호를 의심한다.
 * 세 상태를 각각 적게 만들면 그 붕괴가 **표현 불가능**하다.
 *
 * ─── `ready` 페이로드가 여기 없는 이유 (2026-09-02) ───
 * 그건 `session.bootstrap` 의 출력이고 **두 당사자의 계약**이라 `packages/contracts` 가
 * 소유한다(`session.ts`). 이 파일에는 **클라이언트만의 상태** 둘이 남는다:
 * `anonymous` 와 `unavailable` 은 서버가 보낼 수 있는 값이 아니라 401 과 통신 실패에서 나온다.
 */
import type { BootstrapReady } from '@worhythm/contracts';
import { classifyRpcError, type RpcFailure } from './errors.js';

export type Bootstrap =
  | { readonly kind: 'anonymous' }
  | ({ readonly kind: 'ready' } & BootstrapReady)
  | {
      readonly kind: 'unavailable';
      /** 렌더할 i18n 키. 에러 레지스트리의 `messageKey` 가 그대로 들어온다. */
      readonly messageKey: string;
      readonly nextActionKey?: string;
      /** 개발자용 한 줄. 사용자 문구가 아니라 **왜 못 떴는지**다(스택·SQL 은 넣지 않는다). */
      readonly detail?: string;
    };

/**
 * 부트를 수행하는 포트. composition root(`apps/web`)가 구현을 넘긴다.
 *
 * 왜 포트인가: 부트는 `session.bootstrap` **procedure** 를 부르는 일이고, 그 라우터 타입은
 * vertical 을 합치므로 `packages/**` 가 이름을 부를 수 없다(`core-must-not-know-verticals`).
 * 그래서 코어는 "부트하는 함수"만 알고, 그 함수를 만드는 것은 apps/web 이다 —
 * `MIGRATION_MANIFEST`·`VERTICALS` 가 apps/server 에 있는 것과 같은 이유다.
 */
export type BootstrapPort = () => Promise<Bootstrap>;

/**
 * procedure 호출 하나를 부트 3상태로 접는다.
 *
 * ─── 이 함수가 `apps/web` 이 아니라 여기 있는 이유 ───
 * 분류 규칙(무엇이 anonymous 이고 무엇이 unavailable 인가)은 **코어의 판단**이고
 * 게이트가 보는 곳에 살아야 한다. `apps/web/src` 는 purity 와
 * `core-must-not-know-verticals` 양쪽의 면제 구역이라(G0) 거기 두면 검사받지 않는다.
 * composition root 에는 "무엇을 부르는가"만 남는다 — 그게 조립이다.
 *
 * ─── 401 만 anonymous 다 ───
 * 403·429·500·오프라인은 전부 `unavailable` 이다. 하나로 접으면 서버가 죽은 날
 * 전 사용자에게 로그인 안내가 뜨고, 그건 유니온을 셋으로 나눈 이유 자체를 무효로 만든다.
 * 판정은 `classifyRpcError` 가 이미 한다 — 여기서 status 를 다시 읽지 않는다.
 */
export function bootstrapPortFrom(
  call: () => Promise<BootstrapReady>,
  /**
   * 분류기. **테스트가 쥐는 자리다** — 진짜 `ORPCError` 를 만들려면 `@orpc/client` 를
   * import 해야 하는데 이 패키지는 그럴 수 없다(`orpc-only-in-rpc-kernel`).
   * 여기서 검사할 것은 분류 자체가 아니라 **분류 → 3상태의 대응**이고, 분류는
   * `packages/rpc-client` 가 자기 테스트로 덮는다. 두 책임을 두 곳에서 각각 본다.
   */
  classify: (err: unknown) => RpcFailure = classifyRpcError,
): BootstrapPort {
  return async () => {
    try {
      return { kind: 'ready', ...(await call()) };
    } catch (e) {
      const failure = classify(e);
      if (failure.kind === 'unauthenticated') return { kind: 'anonymous' };
      return {
        kind: 'unavailable',
        messageKey: failure.messageKey,
        nextActionKey: failure.nextActionKey,
        /*
         * 원본 메시지를 그대로 싣지 않는다 — 스택·SQL·파일 경로가 새는 자리가 정확히 여기다
         * (서버의 `toORPCError` 가 미분류 에러에 대해 하는 것과 같은 판단).
         * 코드와 상태만 적는다. 그것으로 로그에서 같은 요청을 찾을 수 있다.
         */
        detail: `${failure.code ?? 'unclassified'}${failure.status ? ` · HTTP ${failure.status}` : ''}`,
      };
    }
  };
}
